import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { DEFAULT_PAGE_SIZE } from '@/__core/constants/pagination';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import {
  Box,
} from '@mui/material';
import * as employee from '../redux/EmployeeRedux';
import { getEmployees, getEmployee, createEmployeeWithUser, updateEmployee as updateEmployeeAPI, getDeletedEmployees, exportEmployees, importEmployees, EmployeeImportResult } from '../redux/EmployeeCRUD';
import { CreateEmployeeRequest } from '../models/Employee';
import { EmployeeModel } from '../models/Employee';
import { useModuleTabsWithGuard } from '@/__core/hooks/useModuleTabsWithGuard';
import { TabData } from '@/__core/types/TabData';
import ModuleTabsHeader from '@/modules/users/components/ModuleTabsHeader';
import AppListToolbarGlobal from '@/modules/employees/components/EmployeesToolbarGlobal';
import MainListModule from '@/__core/layout/module/MainListModule';
import { useGlobalLoading } from '@/__core/hooks/useGlobalLoading';
import { usePermissions } from '@/__core/permissions/hooks/usePermissions';
import {
  useModuleToolbarPermissions,
  isTabVisibleForToolbarCompany,
  resolveEntityCompanyId,
} from '@/__core/permissions';
import { useRealtimePermissions } from '@/__core/permissions/hooks/useRealtimePermissions';
import { RootState } from '@/setup';
import { formatErrorForSnackbar, toApiError } from '@/__core/helpers/Error';
import MessageSnackbar from '@/__core/helpers/components/MessageSnackbar';
import { fetchCompanies } from '../../organizations/redux/CompanyRedux';
import { fetchDepartments } from '../../organizations/redux/DepartmentRedux';
import { getRoles, normalizeRolesPayload } from '../../users/redux/RoleCRUD';
import * as rolesActions from '../../users/redux/RoleRedux';
import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';
import { resolveActiveCompanyFilter, resolveToolbarTabCompanyFilter } from '@/__core/hooks/useCompanyContext';

import MainListEmployee, { MainListEmployeeHandle } from './MainListEmployee';
import NewEmployee from './NewEmployee';
import { resolveEmployeeStatusId } from '../utils/employeeStatus';
import TrashModalEmployee from './TrashModalEmployee';

// Memoized selector to prevent unnecessary rerenders
const selectEmployeesData = createSelector(
  [(state: any) => state.employees],
  (employeesState) => ({
    employees: employeesState?.employees || [],
    allEmployees: employeesState?.employees || [],
    loading: employeesState?.loading || false,
    error: employeesState?.error || null
  })
);

export default function EmployeesModule() {
  usePermissions(); // Ensure permissions context is initialized
  const { refreshPermissions } = useRealtimePermissions(); // Get refresh function
  const dispatch = useDispatch();
  const { employees = [], loading = false, error = null } = useSelector(selectEmployeesData);
  const { startGlobalLoading, stopGlobalLoading } = useGlobalLoading();
  
  // Get companies, departments, and roles from Redux
  const companies = useSelector((state: RootState) => state.company.companies);
  const departments = useSelector((state: RootState) => state.department.departments);
  const roles = useSelector((state: RootState) => state.roles.roles);
  
  // Ensure arrays
  const safeCompanies = Array.isArray(companies) ? companies : [];
  const safeDepartments = Array.isArray(departments) ? departments : [];
  const safeRoles = Array.isArray(roles) ? roles : [];

  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [, setIsUpdating] = useState(false);
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [trashModalOpen, setTrashModalOpen] = useState(false);
  const [deletedItemsCount, setDeletedItemsCount] = useState(0);
  const listRef = useRef<MainListEmployeeHandle>(null);
  const importInputRef = useRef<HTMLInputElement>(null);
  const lastEmployeesFetchKeyRef = useRef('');
  const employeeFilterDataLoadedRef = useRef(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    company: 'all',
    department: 'all',
    role: 'all',
    status: 'all',
  });
  const selectedCompanyId = getCurrentCompanyId() || '';
  const activeCompanyFilter = resolveActiveCompanyFilter(
    selectedCompanyId,
    advancedFilters.company
  );

  const toolbarTabCompanyFilter = resolveToolbarTabCompanyFilter(advancedFilters.company);

  const {
    canRead: canReadEmployees,
    canCreate: canCreateEmployees,
    canDelete: canDeleteEmployees,
    resolveTabPermissions,
    canReadRecord: canReadEmployeeRecord,
    resolvePermissionsForCompanyId: resolveEmployeePermissions,
  } = useModuleToolbarPermissions({
    resource: 'employees',
    activeCompanyFilter,
    updateTabTypes: ['update-employee'],
  });

  // Default tabs configuration - only show add tab if user has create permission
  const defaultTabs: TabData[] = useMemo(() => {
    const tabs: TabData[] = [
      { id: 0, title: 'Danh Sách Nhân Viên', type: 'list', closable: false, createdAt: Date.now() },
    ];
    
    // Chỉ thêm tab "+" nếu user có permission create employee
    if (canCreateEmployees === true) {
      tabs.push({
        id: 10,
        title: '',
        type: 'add',
        closable: false,
        createdAt: Date.now()
      });
    }
    
    return tabs;
  }, [canCreateEmployees]);

  const defaultCompanyForNewEmployee = useMemo(() => {
    const normalizedActiveCompany = String(activeCompanyFilter || '').trim();
    if (
      normalizedActiveCompany &&
      normalizedActiveCompany !== 'all' &&
      normalizedActiveCompany !== 'global'
    ) {
      return normalizedActiveCompany;
    }

    return String(selectedCompanyId || '').trim();
  }, [activeCompanyFilter, selectedCompanyId]);

  const {
    storedTabs,
    activeTab,
    setActiveTab,
    removeTab,
    updateTab,
    tabs,
    visibleActiveTab,
    handleVisibleTabChange,

    isStoredTabPanelActive,
    handleCloseTab,
    handleAddTab,
    handleUnsavedChangesUpdate,
    openTab,
    unsavedDialog,
  } = useModuleTabsWithGuard({
    storageKey: 'employees_module_tabs',
    defaultTabs,
    maxTabs: 20,
    canCreate: canCreateEmployees === true,
    toolbarCompanyId: toolbarTabCompanyFilter,
    guardedTabTypes: ['new-employee', 'update-employee'],
    buildNewTab: (currentTabs) => {
      const nextNumber = currentTabs.filter((tab) => tab.type === 'new-employee').length + 1;
      const companyId = defaultCompanyForNewEmployee || undefined;
      return {
        id: Date.now(),
        title: `Employee #${nextNumber}`,
        type: 'new-employee',
        closable: true,
        metadata: {
          companyId,
          formData: companyId ? { company: companyId } : null,
        },
      };
    },
    onAfterTabChange: () => {
      setSuccessMessage(null);
      setErrorMessage(null);
    },
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<{
    count: number;
    page: number;
    pageSize: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  }>({
    count: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 1,
    hasNext: false,
    hasPrevious: false,
  });

 
  
  const displayEmployees = Array.isArray(employees) ? employees : [];

  // Handle search input change
  const handleSearchInputChange = (value: string) => {
    setSearchInputValue(value);
    // If search box is empty, search immediately (reset to default)
    if (value === '') {
      setCurrentPage(1);
      setSearchQuery('');
    }
  };

  // Handle search submit (Enter key)
  const handleSearchSubmit = () => {
    setCurrentPage(1);
    setSearchQuery(searchInputValue);
  };

  // Reset loading when leaving module
  useEffect(() => {
    return () => {
      stopGlobalLoading('employees-data-load');
      dispatch(employee.actions.failEmployees(''));
      lastEmployeesFetchKeyRef.current = '';
    };
  }, [dispatch, stopGlobalLoading]);

  // Load employees (deduped by filter key — avoids duplicate calls on re-render / Strict Mode)
  useEffect(() => {
    const fetchParams = {
      search: searchQuery,
      company: activeCompanyFilter,
      department: advancedFilters.department,
      role: advancedFilters.role,
      status: advancedFilters.status,
      page: currentPage,
      page_size: pagination.pageSize,
    };
    const fetchKey = JSON.stringify(fetchParams);
    if (lastEmployeesFetchKeyRef.current === fetchKey) {
      return;
    }
    lastEmployeesFetchKeyRef.current = fetchKey;

    const controller = new AbortController();
    dispatch(employee.actions.requestEmployees());
    startGlobalLoading('Đang tải danh sách nhân viên...', 'employees-data-load');
    getEmployees(fetchParams, { signal: controller.signal }).then(response => {
      // Use response.data directly if results is undefined
      const employeesData = response.data.results || response.data || [];
      dispatch(employee.actions.fulfillEmployees(employeesData));
      setPagination({
        count: response.data.count || 0,
        page: response.data.page || currentPage,
        pageSize: response.data.page_size || pagination.pageSize,
        totalPages: response.data.total_pages || Math.max(1, Math.ceil((response.data.count || 0) / Math.max(response.data.page_size || pagination.pageSize, 1))),
        hasNext: response.data.has_next || false,
        hasPrevious: response.data.has_previous || false,
      });
    }).catch(error => {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        dispatch(employee.actions.failEmployees(''));
        return;
      }
      console.error('Error loading employees:', error);
      dispatch(employee.actions.failEmployees(error?.message || 'Failed to load employees'));
    }).finally(() => {
      stopGlobalLoading('employees-data-load');
    });
    return () => {
      controller.abort();
      lastEmployeesFetchKeyRef.current = '';
    };
  }, [searchQuery, activeCompanyFilter, advancedFilters.department, advancedFilters.role, advancedFilters.status, currentPage, pagination.pageSize, dispatch, startGlobalLoading, stopGlobalLoading]);

  // Load filter dropdown data once; skip APIs already in Redux from other modules
  useEffect(() => {
    if (employeeFilterDataLoadedRef.current) {
      return;
    }
    employeeFilterDataLoadedRef.current = true;

    queueMicrotask(() => {
      if (safeCompanies.length === 0) {
        dispatch(fetchCompanies());
      }
      if (safeDepartments.length === 0) {
        dispatch(fetchDepartments());
      }
      if (safeRoles.length === 0) {
        dispatch(rolesActions.actions.requestRoles() as any);
        getRoles()
          .then((response) => {
            dispatch(
              rolesActions.actions.fulfillRoles(normalizeRolesPayload(response.data)) as any
            );
          })
          .catch((error) => {
            console.error('Error loading roles:', error);
            dispatch(rolesActions.actions.failRoles(error.message) as any);
          });
      }
      fetchDeletedCount();
    });
  }, [dispatch, safeCompanies.length, safeDepartments.length, safeRoles.length]);

  // Memoize current update employee data to prevent unnecessary re-renders
  const currentUpdateEmployee = useMemo(() => {
    if (!storedTabs || !Array.isArray(employees)) return null;
    
    if (storedTabs[activeTab]?.type === 'update-employee' && storedTabs[activeTab]?.entityId) {
      const tabEmployee = storedTabs[activeTab].entityData as any;
      const storeEmployee = employees.find((e: EmployeeModel) => e.id === storedTabs[activeTab].entityId) as any;

      // Prefer tab entity data because handleRowClick fetches full employee detail
      // while list store may contain condensed payload.
      const employee = tabEmployee || storeEmployee;
      
      if (employee) {
        const resolveCompanyId = (value: any): string => {
          if (!value) return '';
          if (typeof value === 'object') return String(value.id || value.company_id || '');
          const normalized = String(value).trim();
          if (!normalized) return '';
          const byId = safeCompanies.find((c: any) => String(c.id) === normalized);
          if (byId) return String(byId.id);
          const byName = safeCompanies.find((c: any) => String(c.name || '').trim() === normalized);
          return byName ? String(byName.id) : normalized;
        };

        const resolveDepartmentId = (value: any): string => {
          if (!value) return '';
          if (typeof value === 'object') return String(value.id || value.department_id || '');
          const normalized = String(value).trim();
          if (!normalized) return '';
          const byId = safeDepartments.find((d: any) => String(d.id) === normalized);
          if (byId) return String(byId.id);
          const byName = safeDepartments.find((d: any) => String(d.name || '').trim() === normalized);
          return byName ? String(byName.id) : normalized;
        };

        const employeeAny = employee as any;
        const rawUser = employeeAny?.user && typeof employeeAny.user === 'object' ? employeeAny.user : null;
        const resolvedUserId = String(rawUser?.id || employeeAny?.user_id || employeeAny?.userId || '').trim();
        const resolvedUserEmail = String(
          rawUser?.email || employeeAny?.user_email || employeeAny?.email || ''
        ).trim();
        const resolvedUserFirstName = String(
          rawUser?.first_name || employeeAny?.user_first_name || employeeAny?.first_name || ''
        ).trim();
        const resolvedUserLastName = String(
          rawUser?.last_name || employeeAny?.user_last_name || employeeAny?.last_name || ''
        ).trim();
        const resolvedUserUsername = String(
          rawUser?.username || employeeAny?.user_username || employeeAny?.user_name || resolvedUserEmail.split('@')[0] || ''
        ).trim();
        const hasLinkedUser = !!(rawUser || resolvedUserId || resolvedUserEmail);

        const mappedEmployee = {
          id: employee.id || '',
          name: employee.name || '',
          nickname: employeeAny?.nickname || '',
          code: employee.code || '',
          email: resolvedUserEmail || employeeAny?.emp_email || employee.email || '',
          emp_email: employeeAny?.emp_email || '',
          fullName: employee.name || '',
          status: resolveEmployeeStatusId(
            employee.status ?? employeeAny?.status_id,
            'active'
          ),
          jobTitle:
            (employee.jobTitle && typeof employee.jobTitle === 'object'
              ? employee.jobTitle.id || ''
              : employee.jobTitle) ||
            (employeeAny?.job_title && typeof employeeAny.job_title === 'object'
              ? String(employeeAny.job_title.id || '')
              : String(employeeAny?.job_title || '')),
          department: resolveDepartmentId(employee.department), // Use ID for form
          company: resolveCompanyId(employee.company), // Add company ID
          area: employee.area && typeof employee.area === 'object' ? employee.area.id || '' : employee.area || '', // Add area
          office: employee.office && typeof employee.office === 'object' ? employee.office.id || '' : employee.office || '', // Add office
          position: employee.position && typeof employee.position === 'object' ? employee.position.id || '' : employee.position || '', // Add position
          address: employee.address || { address_line: '', province: '', ward: '', free_address: '' }, // Add address object
          lastLogin: rawUser?.last_login || employeeAny?.user_last_login || '',
            dateOfBirth: employeeAny?.date_of_birth || '',
            gender: employeeAny?.gender || '',
            phone: employeeAny?.phone || '',
            startDate: employeeAny?.start_date || '',
            resignDate: employeeAny?.resign_date || '',
            bankAccountName: employeeAny?.bank_account_name || '',
            bankAccountNumber: employeeAny?.bank_account_number || '',
            bankName: employeeAny?.bank_name || '',
            notes: employeeAny?.notes || '',
            relativeInfo: employeeAny?.relative_info || '',
            image1: employeeAny?.image1 || '',
            image2: employeeAny?.image2 || '',
              attendance_type: employeeAny?.attendance_type || 'attendance',
            missions: Array.isArray(employeeAny?.missions)
              ? employeeAny.missions.map((m: any) => (m && typeof m === 'object' ? m.code : m)).filter(Boolean)
              : [],
            dashboards: Array.isArray(employeeAny?.dashboards)
              ? employeeAny.dashboards.map((d: any) => (d && typeof d === 'object' ? d.code : d)).filter(Boolean)
              : [],
            primary_salary_config_group: employeeAny?.primary_salary_config_group || employeeAny?.salary_config_group || null,
            secondary_salary_config_groups: Array.isArray(employeeAny?.secondary_salary_config_groups)
              ? employeeAny.secondary_salary_config_groups
              : (Array.isArray(employeeAny?.salary_config_groups_secondary)
                  ? employeeAny.salary_config_groups_secondary
                  : []),
          user: hasLinkedUser ? {
            id: resolvedUserId,
            email: resolvedUserEmail,
            first_name: resolvedUserFirstName || rawUser?.name?.split(' ')[0] || rawUser?.full_name?.split(' ')[0] || '',
            last_name: resolvedUserLastName || rawUser?.name?.split(' ').slice(1).join(' ') || rawUser?.full_name?.split(' ').slice(1).join(' ') || '',
            username: resolvedUserUsername,
          } : undefined,
        };
        return mappedEmployee;
      }
    }
    return null;
  }, [storedTabs, activeTab, employees, safeCompanies, safeDepartments]); // Only recalculate when these change

  const handleSaveNewEmployee = async (employeeData: any) => {
    const createCompanyId =
      String(employeeData.company || '').trim() ||
      defaultCompanyForNewEmployee ||
      activeCompanyFilter;
    const createPermissions = resolveEmployeePermissions(createCompanyId);

    if (!createPermissions.canCreate) {
      setErrorMessage("Bạn không có quyền tạo nhân viên mới");
      return;
    }
    
    // Clear previous error message
    setErrorMessage(null);
    
    // Split fullName into first_name and last_name
    const fullName = employeeData.fullName || '';
    const nameParts = fullName.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    
    let finalSelectedUserId = employeeData.selectedUserId;
    let finalAddressId: string | undefined = undefined; // Initialize as undefined to ensure we use created address
    
    const normalizedAddressObj =
      employeeData.address_obj && typeof employeeData.address_obj === 'object'
        ? employeeData.address_obj
        : null;
    const hasMeaningfulAddress = !!(
      normalizedAddressObj &&
      (
        String(normalizedAddressObj.address_line || '').trim() ||
        String(normalizedAddressObj.ward || '').trim() ||
        String(normalizedAddressObj.province || normalizedAddressObj.province_code || '').trim() ||
        String(normalizedAddressObj.postal_code || '').trim() ||
        String(normalizedAddressObj.free_address || '').trim()
      )
    );

    // Nếu có address_obj hợp lệ, tạo address trước
    if (hasMeaningfulAddress && normalizedAddressObj) {
      try {
        
        // Import createAddress
        const { createAddress } = await import('@/modules/addresses/redux/AddressCRUD');
        
        // Prepare address data - ensure it matches AddressFormData format
        // AddressFormData requires: address_line, ward, province (province_code), optional postal_code
        const addressData = {
          address_line: normalizedAddressObj.address_line || normalizedAddressObj.free_address || '',
          ward: normalizedAddressObj.ward || '',
          province: normalizedAddressObj.province || normalizedAddressObj.province_code || '',
          postal_code: normalizedAddressObj.postal_code || '',
        };
        
        
        // Create address
        const addressResponse = await createAddress(addressData);
        const createdAddress = addressResponse.data;
        
        // Use the created address ID for employee creation
        finalAddressId = createdAddress.id;
        
      } catch (addressError: any) {
        const errorMessage = formatErrorForSnackbar(addressError) || 'Có lỗi xảy ra khi tạo địa chỉ';
        setErrorMessage(errorMessage);
        throw addressError; // Re-throw to prevent employee creation
      }
    }
    
    // Nếu tạo user mới và có roles, tạo user trước, update roles, sau đó mới tạo employee
    if (employeeData.userSelectionType === "new" && employeeData.roles && Array.isArray(employeeData.roles) && employeeData.roles.length > 0) {
      try {
        
        // Import createUser and updateUser
        const { createUser } = await import('@/modules/users/redux/UserCRUD');
        const { updateUser } = await import('@/modules/users/redux/UserCRUD');
        
        // Create user first (without roles, as some backends might not support roles in create)
        const username = employeeData.email?.split('@')[0] || employeeData.email || '';
        const companyId =
          String(employeeData.company || '').trim() ||
          String(defaultCompanyForNewEmployee || '').trim() ||
          getCurrentCompanyId() || '';
        
        const createUserData = {
          username: username,
          email: employeeData.email,
          first_name: firstName,
          last_name: lastName,
          password: employeeData.password,
          confirm_password: employeeData.confirmPassword,
          company: companyId,
          status: 'active' as const,
        };
        
        const userResponse = await createUser(createUserData);
        const createdUser = userResponse.data;
        
        // Update roles for the newly created user
        if (createdUser.id && employeeData.roles.length > 0) {
          await updateUser(createdUser.id, {
            company: companyId,
            roles: employeeData.roles
          });
        }
        
        // Use the created user ID for employee creation
        // Change userSelectionType to "existing" since we've already created the user
        finalSelectedUserId = createdUser.id;
        employeeData.userSelectionType = "existing"; // Update to use existing user
        
      } catch (userError: any) {
        const errorMessage = formatErrorForSnackbar(userError) || 'Có lỗi xảy ra khi tạo user hoặc cập nhật roles';
        setErrorMessage(errorMessage);
        throw userError; // Re-throw to prevent employee creation
      }
    }
    
    const createEmployeeData: CreateEmployeeRequest = {
      name: employeeData.name,
      nickname: employeeData.nickname !== undefined ? employeeData.nickname : '',
      code: employeeData.code,
      status: employeeData.status,
      attendance_type: employeeData.attendance_type || 'attendance',
      job_title_id: employeeData.job_title,
      area_id: employeeData.area,
      office_id: employeeData.office,
      position_id: employeeData.position,
      company_id:
        String(employeeData.company || '').trim() ||
        String(defaultCompanyForNewEmployee || '').trim() ||
        undefined,
      department_id: employeeData.department,
      email: employeeData.email,
      password: employeeData.password,
      confirm_password: employeeData.confirmPassword,
      first_name: firstName,
      last_name: lastName,
      // Add user selection data - use updated userSelectionType (may be changed to "existing" if user was created)
      userSelectionType: employeeData.userSelectionType,
      selectedUserId: finalSelectedUserId,
    };
    
    // Only add address_id when we created a valid address, otherwise explicitly send null.
    if (finalAddressId) {
      createEmployeeData.address_id = finalAddressId;
    } else {
      (createEmployeeData as any).address_id = null;
    }
    
    createEmployeeData.date_of_birth = employeeData.date_of_birth || null;
    createEmployeeData.gender = employeeData.gender ?? '';
    createEmployeeData.phone = employeeData.phone ?? '';
    createEmployeeData.start_date = employeeData.start_date || null;
    createEmployeeData.resign_date = employeeData.resign_date || null;
    createEmployeeData.bank_account_name = employeeData.bank_account_name ?? '';
    createEmployeeData.bank_account_number = employeeData.bank_account_number ?? '';
    createEmployeeData.bank_name = employeeData.bank_name ?? '';
    createEmployeeData.notes = employeeData.notes ?? '';
    createEmployeeData.relative_info = employeeData.relative_info ?? '';
    createEmployeeData.image1 = employeeData.image1 ?? '';
    createEmployeeData.image2 = employeeData.image2 ?? '';
    createEmployeeData.missions = Array.isArray(employeeData.missions)
      ? employeeData.missions
      : [];
    createEmployeeData.dashboards = Array.isArray(employeeData.dashboards)
      ? employeeData.dashboards
      : [];
    createEmployeeData.primary_salary_config_group = employeeData.primary_salary_config_group ?? null;
    createEmployeeData.secondary_salary_config_groups = Array.isArray(employeeData.secondary_salary_config_groups)
      ? employeeData.secondary_salary_config_groups
      : [];
  
    
    try {
      const response = await createEmployeeWithUser(createEmployeeData);
      const createdEmployeeData = response.data as any;
      
      // Extract employee data from response
      let createdEmployee = createdEmployeeData?.employee || createdEmployeeData;
      
      // If response has separate user object, merge it into employee
      if (createdEmployeeData?.user && !createdEmployee.user) {
        createdEmployee = {
          ...createdEmployee,
          user: createdEmployeeData.user
        };
      }
      
      // If employee has ID but no user data or no address data, fetch full employee data from API
      const needsFetch = !createdEmployee?.user || (!createdEmployee?.address && finalAddressId);
      if (createdEmployee?.id && needsFetch) {
        try {
          
          const { getEmployee } = await import('../redux/EmployeeCRUD');
          const fullEmployeeResponse = await getEmployee(createdEmployee.id);
          if (fullEmployeeResponse?.data) {
            createdEmployee = fullEmployeeResponse.data;
          }
        } catch (fetchError) {
          console.warn('Could not fetch full employee data after creation:', fetchError);
        }
      }
      
      // If employee has address_id but no address object, try to fetch address separately
      if (createdEmployee?.id && !createdEmployee?.address && finalAddressId) {
        try {
          
          const { getAddress } = await import('@/modules/addresses/redux/AddressCRUD');
          const addressResponse = await getAddress(finalAddressId);
          if (addressResponse?.data) {
            createdEmployee = {
              ...createdEmployee,
              address: addressResponse.data,
            };
          }
        } catch (addressFetchError) {
          console.warn('Could not fetch address data:', addressFetchError);
        }
      }

      if (createdEmployee) {
        createdEmployee = {
          ...createdEmployee,
          jobTitle:
            createdEmployee.jobTitle ||
            (createdEmployee as any).job_title ||
            undefined,
        };
      }
      
      // Dispatch success action with just the employee data
      if (createdEmployee) {
        dispatch(employee.actions.createEmployee(createdEmployee));
      }
      
      // Update tab to become an update tab with the created employee data
      const currentTab = storedTabs[activeTab];
      if (currentTab && currentTab.type === 'new-employee') {
        const employeeName = createdEmployee?.name || createdEmployee?.code || 'Nhân viên mới';
        
        updateTab(currentTab.id, {
          title: employeeName,
          type: 'update-employee',
          entityId: createdEmployee?.id,
          entityType: 'employee',
          entityData: createdEmployee
        });
      }
      
      // Return response data to update form
      return { employee: createdEmployee, ...createdEmployeeData };
    } catch (error: any) {
      // Use formatErrorForSnackbar helper to display error messages
      const errorMessage = formatErrorForSnackbar(error) || 'Có lỗi xảy ra khi tạo nhân viên';
      setErrorMessage(errorMessage);
    }
    
  };



  const handleCancelNewEmployee = () => {
    // Remove the current new employee tab
    if (!storedTabs) return;
    
    const currentTab = storedTabs[activeTab];
    if (currentTab && currentTab.type === 'new-employee') {
      removeTab(currentTab.id);
      setActiveTab(0);
    }
  };

  const handleRowClick = async (employee: any) => {
    if (!canReadEmployeeRecord(employee)) {
      setErrorMessage("Bạn không có quyền xem chi tiết nhân viên");
      return;
    }
    // Clear success message when opening update tab
    setSuccessMessage(null);
    
    if (!storedTabs) return;
    
    // Check if tab already exists for this employee
    const existingTabIndex = storedTabs.findIndex(tab => 
      tab.type === 'update-employee' && tab.entityId === employee.id
    );
    
    if (existingTabIndex !== -1) {
      // Tab exists: refresh with full detail so fields are always mapped correctly.
      const existingTab = storedTabs[existingTabIndex];
      try {
        const detailResponse = await getEmployee(String(employee.id));
        if (detailResponse?.data && existingTab) {
          const entityCompanyId = resolveEntityCompanyId(detailResponse.data);
          updateTab(existingTab.id, {
            ...existingTab,
            entityData: detailResponse.data,
            metadata: {
              ...existingTab.metadata,
              companyId: entityCompanyId || existingTab.metadata?.companyId,
            },
          });
        }
      } catch {
        // Keep existing tab data if detail request fails.
      }

      setActiveTab(existingTabIndex);
    } else {
      let fullEmployee = Array.isArray(employees)
        ? employees.find((e: EmployeeModel) => String(e.id) === String(employee.id))
        : undefined;

      // Always try to fetch full employee detail before opening edit tab.
      // List API may return condensed user payload, causing edit form mapping gaps.
      try {
        const detailResponse = await getEmployee(String(employee.id));
        if (detailResponse?.data) {
          fullEmployee = detailResponse.data as any;
        }
      } catch {
        // Fallback to currently available list row data.
      }

      const entityCompanyId =
        resolveEntityCompanyId(fullEmployee || employee) || undefined;

      openTab({
        id: Date.now(),
        title: `${employee.name || employee.email}`,
        closable: true,
        type: 'update-employee',
        entityId: employee.id,
        entityType: 'employee',
        entityData: fullEmployee || employee,
        metadata: {
          companyId: entityCompanyId,
        },
      });
    }
  };

  const handleSaveUpdateEmployee = async (employeeData: any, employeeId?: string) => {
    let id = employeeId;

    if (!id) {
      if (!storedTabs) return;

      const currentTab = storedTabs[activeTab];
      if (!currentTab || currentTab.type !== 'update-employee' || !currentTab.entityId) {
        return;
      }

      id = currentTab.entityId;
    }

    const updatePermissions = resolveEmployeePermissions(
      employeeData.company_id ||
        resolveEntityCompanyId(
          storedTabs.find(
            (tab) => tab.type === 'update-employee' && tab.entityId === id
          )?.entityData
        )
    );

    if (!updatePermissions.canUpdate) {
      setErrorMessage("Bạn không có quyền cập nhật nhân viên");
      return;
    }

    // Set updating flag
    setIsUpdating(true);
    setSuccessMessage(null);
    
    // Use provided employee ID or find from current tab
    // Split fullName into first_name and last_name
    const fullName = employeeData.fullName || '';
    const nameParts = fullName.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    
    const updateEmployeeData: any = {
      name: employeeData.name,
      code: employeeData.code,
      status: employeeData.status,
      attendance_type: employeeData.attendance_type || 'attendance',
      job_title_id: employeeData.job_title_id,
      area_id: employeeData.area_id,
      office_id: employeeData.office_id,
      position_id: employeeData.position_id,
      company_id: employeeData.company_id,
      email: employeeData.email,
      first_name: firstName,
      last_name: lastName,
      // Ensure nickname is always included
      nickname: employeeData.nickname !== undefined ? employeeData.nickname : "",
      date_of_birth: employeeData.date_of_birth || null,
      gender: employeeData.gender ?? '',
      phone: employeeData.phone ?? '',
      start_date: employeeData.start_date || null,
      resign_date: employeeData.resign_date || null,
      bank_account_name: employeeData.bank_account_name ?? '',
      bank_account_number: employeeData.bank_account_number ?? '',
      bank_name: employeeData.bank_name ?? '',
      notes: employeeData.notes ?? '',
      relative_info: employeeData.relative_info ?? '',
      image1: employeeData.image1 ?? '',
      image2: employeeData.image2 ?? '',
      missions: Array.isArray(employeeData.missions)
        ? employeeData.missions
        : [],
      dashboards: Array.isArray(employeeData.dashboards)
        ? employeeData.dashboards
        : [],
    };

    // Preserve user handling option in update payload.
    if (employeeData.userSelectionType === 'no-user') {
      updateEmployeeData.userSelectionType = 'no-user';
      if (employeeData.emp_email && String(employeeData.emp_email).trim() !== '') {
        updateEmployeeData.emp_email = String(employeeData.emp_email).trim();
      } else if (employeeData.email && String(employeeData.email).trim() !== '') {
        updateEmployeeData.emp_email = String(employeeData.email).trim();
      }
      // Do not send linked-user email when no-user option is selected.
      delete updateEmployeeData.email;
    }
    
    // Only add department_id if it has a valid value (not empty string)
    if (employeeData.department_id && employeeData.department_id.trim() !== '') {
      updateEmployeeData.department_id = employeeData.department_id;
    }
    
    // Add address_obj if it exists
    if (employeeData.address_obj) {
      updateEmployeeData.address_obj = employeeData.address_obj;
    } else {
      console.log("No address_obj found in employeeData");
    }

    // Add roles if they exist
    if (employeeData.roles && Array.isArray(employeeData.roles) && employeeData.roles.length > 0) {
      updateEmployeeData.roles = employeeData.roles;
    }

    // Add salary config group fields
    updateEmployeeData.primary_salary_config_group = employeeData.primary_salary_config_group ?? null;
    updateEmployeeData.secondary_salary_config_groups = Array.isArray(employeeData.secondary_salary_config_groups)
      ? employeeData.secondary_salary_config_groups
      : [];

    try {
      const response = await updateEmployeeAPI(id, updateEmployeeData);
      const updatedEmployee = response.data;

      // Re-fetch the employee to get full nested data (department, company, jobTitle objects).
      // The update API response often omits nested objects, causing the form to show empty
      // dropdowns when the tab is reopened.
      try {
        const freshResponse = await getEmployee(id);
        dispatch(employee.actions.updateEmployee(freshResponse.data));
        return freshResponse.data;
      } catch {
        dispatch(employee.actions.updateEmployee(updatedEmployee));
        return updatedEmployee;
      }
    } catch (error) {
      throw error;
    }
  };

  const handleCancelUpdateEmployee = () => {
    // Remove the current update employee tab
    if (!storedTabs) return;
    
    const currentTab = storedTabs[activeTab];
    if (currentTab && currentTab.type === 'update-employee') {
      removeTab(currentTab.id);
      setActiveTab(0);
    }
  };



  const handleRefresh = useCallback(() => {
    lastEmployeesFetchKeyRef.current = '';
    dispatch(employee.actions.requestEmployees());
    startGlobalLoading('Đang tải lại danh sách nhân viên...', 'employees-data-load');
    getEmployees({
      search: searchQuery,
      company: activeCompanyFilter,
      department: advancedFilters.department,
      role: advancedFilters.role,
      status: advancedFilters.status,
      page: currentPage,
      page_size: pagination.pageSize,
    }).then(response => {
      dispatch(employee.actions.fulfillEmployees(response.data.results || []));
      setPagination({
        count: response.data.count || 0,
        page: response.data.page || currentPage,
        pageSize: response.data.page_size || pagination.pageSize,
        totalPages: response.data.total_pages || Math.max(1, Math.ceil((response.data.count || 0) / Math.max(response.data.page_size || pagination.pageSize, 1))),
        hasNext: response.data.has_next || false,
        hasPrevious: response.data.has_previous || false,
      });
    }).catch(error => {
      console.error('Error refreshing employees:', error);
    }).finally(() => {
      stopGlobalLoading('employees-data-load');
    });
    refreshPermissions();
  }, [
    activeCompanyFilter,
    advancedFilters.department,
    advancedFilters.role,
    advancedFilters.status,
    currentPage,
    dispatch,
    pagination.pageSize,
    refreshPermissions,
    searchQuery,
    startGlobalLoading,
    stopGlobalLoading,
  ]);

  const handleEmployeesDeleted = useCallback(() => {
    handleRefresh();
    fetchDeletedCount();
  }, [handleRefresh]);

  const handleOpenTrash = () => {
    setTrashModalOpen(true);
  };

  const handleExport = useCallback(async () => {
    if (!canReadEmployees) {
      return;
    }

    setSuccessMessage(null);
    setErrorMessage(null);
    startGlobalLoading('Đang xuất danh sách nhân viên...', 'employees-export');

    try {
      const response = await exportEmployees({
        company: activeCompanyFilter || 'all',
      });

      const exportedCountHeader =
        response.headers?.['x-employee-export-count'];
      const exportedCount =
        exportedCountHeader !== undefined && exportedCountHeader !== null
          ? Number(exportedCountHeader)
          : null;

      let fileName = `NhanVien_${new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[-:T]/g, '')}.xlsx`;
      const disposition = response.headers?.['content-disposition'] as
        | string
        | undefined;
      if (disposition) {
        const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
        const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
        if (utf8Match?.[1]) {
          try {
            fileName = decodeURIComponent(utf8Match[1]);
          } catch {
            fileName = utf8Match[1];
          }
        } else if (plainMatch?.[1]) {
          fileName = plainMatch[1];
        }
      }

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      if (exportedCount === 0) {
        setErrorMessage('Không có dữ liệu để xuất');
      } else {
        setSuccessMessage('Xuất danh sách nhân viên thành công!');
      }
    } catch (error) {
      setErrorMessage(
        formatErrorForSnackbar(toApiError(error)) ||
          'Có lỗi xảy ra khi xuất danh sách nhân viên!'
      );
    } finally {
      stopGlobalLoading('employees-export');
    }
  }, [
    activeCompanyFilter,
    canReadEmployees,
    startGlobalLoading,
    stopGlobalLoading,
  ]);

  const handleImportClick = useCallback(() => {
    if (!canCreateEmployees) {
      setSuccessMessage(null);
      setErrorMessage('Bạn không có quyền import nhân viên');
      return;
    }

    const targetCompany = String(activeCompanyFilter || '').trim();
    if (!targetCompany || targetCompany === 'all' || targetCompany === 'global') {
      setSuccessMessage(null);
      setErrorMessage('Vui lòng chọn một công ty');
      return;
    }

    setSuccessMessage(null);
    setErrorMessage(null);
    importInputRef.current?.click();
  }, [activeCompanyFilter, canCreateEmployees]);

  const handleImportFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) {
        return;
      }

      const targetCompany = String(activeCompanyFilter || '').trim();
      if (!targetCompany || targetCompany === 'all' || targetCompany === 'global') {
        setErrorMessage('Vui lòng chọn một công ty');
        return;
      }

      setSuccessMessage(null);
      setErrorMessage(null);
      startGlobalLoading('Đang import nhân viên...', 'employees-import');

      try {
        const response = await importEmployees(file, targetCompany);
        const summary: EmployeeImportResult = response.data;
        const created = summary?.created ?? 0;
        const skippedCount = summary?.skipped?.length ?? 0;
        const errorCount = summary?.errors?.length ?? 0;
        const warningCount = summary?.warnings?.length ?? 0;

        const message =
          `Import hoàn tất: tạo ${created}, bỏ qua ${skippedCount}, lỗi ${errorCount}` +
          (warningCount ? `, cảnh báo ${warningCount}` : '') +
          '.';

        if (errorCount > 0 && created === 0) {
          setErrorMessage(message);
        } else {
          setSuccessMessage(message);
        }

        handleRefresh();
      } catch (error) {
        setErrorMessage(
          formatErrorForSnackbar(toApiError(error)) ||
            'Có lỗi xảy ra khi import danh sách nhân viên!'
        );
      } finally {
        stopGlobalLoading('employees-import');
      }
    },
    [activeCompanyFilter, handleRefresh, startGlobalLoading, stopGlobalLoading]
  );

  const handleCloseTrash = () => {
    setTrashModalOpen(false);
  };

  const handleTrashRefresh = () => {
    handleRefresh();
    fetchDeletedCount();
  };

  const fetchDeletedCount = useCallback(async () => {
    try {
      const query =
        activeCompanyFilter &&
        activeCompanyFilter !== 'all' &&
        activeCompanyFilter !== 'global'
          ? { company: activeCompanyFilter }
          : undefined;
      const response = await getDeletedEmployees(query);
      const employees = response.data?.results || response.data || [];
      setDeletedItemsCount(Array.isArray(employees) ? employees.length : 0);
    } catch (error) {
      console.error('Error fetching deleted employees count:', error);
      setDeletedItemsCount(0);
    }
  }, [activeCompanyFilter]);

  const handleToggleAdvancedSearch = () => {
    setIsAdvancedSearchOpen(!isAdvancedSearchOpen);
  };

  const handleAdvancedFilterChange = (field: string, value: string) => {
    setCurrentPage(1);
    lastEmployeesFetchKeyRef.current = '';
    setAdvancedFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };


  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (pageSize: number) => {
    setCurrentPage(1);
    setPagination((prev) => ({
      ...prev,
      page: 1,
      pageSize,
    }));
  };


  const mapEmployeeListRow = useCallback(
    (employee: EmployeeModel) => {
      const employeeAny = employee as any;
      const statusSource = employeeAny?.status ?? employeeAny?.status_id ?? employee.status;
      const statusObject =
        statusSource && typeof statusSource === 'object'
          ? (statusSource as { name?: string; color?: string })
          : null;

      return {
        id: employee.id || '',
        name: employee.name || '',
        nickname: employee.nickname || undefined,
        avatar: (employee.user as any)?.avatar || employeeAny?.image1 || undefined,
        image1: employeeAny?.image1 || undefined,
        userId: employee.user?.id || undefined,
        code: employee.code || '',
        phone: employeeAny?.phone || '',
        email: employee.user?.email || employeeAny?.emp_email || employee.user_email || '',
        missions: Array.isArray(employeeAny?.missions)
          ? employeeAny.missions
            .map((mission: any) => {
              if (typeof mission === 'string') return mission;
              return mission?.name || mission?.code || '';
            })
            .filter(Boolean)
          : [],
        startDate: employeeAny?.start_date || null,
        jobTitle:
          employee.jobTitle?.name ||
          employeeAny?.job_title_name ||
          employeeAny?.job_title?.name ||
          employeeAny?.job_title ||
          '',
        status: resolveEmployeeStatusId(statusSource, 'active'),
        statusName:
          statusObject?.name ||
          employeeAny?.status_name ||
          employeeAny?.status_label ||
          '',
        statusColor:
          statusObject?.color ||
          employeeAny?.status_color ||
          employeeAny?.employee_status?.color ||
          undefined,
        attendance_type: employeeAny?.attendance_type || 'attendance',
        department: employee.department?.name || 'N/A',
        company: employee.company?.name || 'N/A',
      };
    },
    []
  );

  return (
    <>
      <ModuleTabsHeader
        tabs={tabs}
        activeTab={visibleActiveTab}
        currentTabType={storedTabs[activeTab]?.type}
        onTabChange={handleVisibleTabChange}
        onCloseTab={handleCloseTab}
        onAddTab={handleAddTab}
        moduleType="employees"
        unsavedDialog={unsavedDialog}
      />

      {/* Success Message */}
      <MessageSnackbar
        open={!!successMessage}
        message={successMessage || ''}
        severity="success"
        onClose={() => setSuccessMessage(null)}
      />

      {/* Error Message */}
      <MessageSnackbar
        open={!!errorMessage}
        message={errorMessage || ''}
        severity="error"
        onClose={() => setErrorMessage(null)}
      />

      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', width: '100%' }}>
        {/* Main Content */}
        <Box sx={{ flex: 1, display: 'flex' }}>
          {/* Main Content Area */}
          <Box sx={{ position: 'relative', width: '100%' }}>
            {/* Render all tabs but only show active one */}
            {storedTabs && storedTabs.map((tab, index) => {
              if (!isTabVisibleForToolbarCompany(tab, toolbarTabCompanyFilter)) {
                return null;
              }

              const isActive = isStoredTabPanelActive(tab);
              const tabPermissions = resolveTabPermissions(tab);
              
              return (
                <Box
                  key={tab.id}
                  sx={{
                    display: isActive ? 'flex' : 'none',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  {tab.type === 'new-employee' ? (
                    // New Employee Form
                    <NewEmployee
                      key={`new-employee-${tab.id}`}
                      onSave={handleSaveNewEmployee}
                      onUpdate={handleSaveUpdateEmployee}
                      onCancel={handleCancelNewEmployee}
                      activeCompanyFilter={activeCompanyFilter}
                      loading={loading}
                      error={errorMessage || error}
                      successMessage={successMessage}
                      onClearError={() => setErrorMessage(null)}
                      onClearSuccess={() => setSuccessMessage(null)}
                      onUnsavedChanges={handleUnsavedChangesUpdate}
                      isActive={isActive}
                      tabId={tab.id}
                      initialFormData={tab.metadata?.formData}
                      onFormDataChange={(formData) => {
                        updateTab(tab.id, {
                          ...tab,
                          metadata: {
                            ...tab.metadata,
                            companyId:
                              String(formData?.company || '').trim() ||
                              tab.metadata?.companyId,
                            formData: formData
                          }
                        });
                      }}
                      canRead={tabPermissions.canRead}
                      canCreate={tabPermissions.canCreate}
                      canUpdate={tabPermissions.canUpdate}
                      canDelete={tabPermissions.canDelete}
                    />
                  ) : tab.type === 'update-employee' ? (
                    // Update Employee Form
                    (() => {
                      if (!currentUpdateEmployee) {
                        return <div>Employee not found</div>;
                      }
                      
                      return (
                        <NewEmployee
                          key={`update-employee-${tab.id}`}
                          employee={currentUpdateEmployee}
                          onSave={handleSaveNewEmployee}
                          onUpdate={handleSaveUpdateEmployee}
                          onCancel={handleCancelUpdateEmployee}
                          activeCompanyFilter={activeCompanyFilter}
                          loading={loading}
                          error={errorMessage || error}
                          successMessage={successMessage}
                          onClearError={() => setErrorMessage(null)}
                          onClearSuccess={() => setSuccessMessage(null)}
                          isEditMode={true}
                          onUnsavedChanges={handleUnsavedChangesUpdate}
                          isActive={isActive}
                          tabId={tab.id}
                          canRead={tabPermissions.canRead}
                          canCreate={tabPermissions.canCreate}
                          canUpdate={tabPermissions.canUpdate}
                          canDelete={tabPermissions.canDelete}
                        />
                      );
                    })()
                  ) : tab.type === 'list' ? (
                    // List View
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
                      <MainListModule
                        leftSidebar={null}
                        toolbar={
                          <AppListToolbarGlobal
                            searchQuery={searchInputValue}
                            onSearchChange={handleSearchInputChange}
                            onSearchSubmit={handleSearchSubmit}
                            selectedCount={selectedEmployeeIds.length}
                            onToggleAdvancedSearch={handleToggleAdvancedSearch}
                            isAdvancedSearchOpen={isAdvancedSearchOpen}
                            filters={advancedFilters}
                            onFilterChange={handleAdvancedFilterChange}
                            onDelete={() => listRef.current?.requestDelete()}
                            onExport={handleExport}
                            canExport={canReadEmployees}
                            onImport={handleImportClick}
                            canImport={canCreateEmployees === true}
                            onOpenTrash={handleOpenTrash}
                            deletedItemsCount={deletedItemsCount}
                            canDelete={canDeleteEmployees}
                            departments={safeDepartments.map((d: any) => ({
                              id: String(d.id),
                              name: d.name,
                            }))}
                            roles={safeRoles.map((r: any) => ({
                              id: String(r.id),
                              display_name: r.display_name || r.name || '',
                            }))}
                          />
                        }
                        dataTable={
                          <MainListEmployee
                            ref={listRef}
                            activeCompanyFilter={activeCompanyFilter || 'all'}
                            employees={
                              Array.isArray(displayEmployees)
                                ? displayEmployees.map(mapEmployeeListRow)
                                : []
                            }
                            pagination={pagination}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            onRowClick={handleRowClick}
                            onReadDenied={setErrorMessage}
                            onSelectionChange={setSelectedEmployeeIds}
                            onDeleted={handleEmployeesDeleted}
                            onDeleteSuccess={setSuccessMessage}
                            onDeleteError={setErrorMessage}
                            canDelete={canDeleteEmployees}
                            canRead={canReadEmployees}
                          />
                        }
                      />
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Trash Modal */}
      <TrashModalEmployee
        open={trashModalOpen}
        onClose={handleCloseTrash}
        onRefresh={handleTrashRefresh}
      />

      {/* Hidden file input for Excel import */}
      <input
        ref={importInputRef}
        type="file"
        accept=".xlsx"
        style={{ display: 'none' }}
        onChange={handleImportFileChange}
      />

    </>
  );
}
